import { FormGroup as NewFormGroup } from '@ksyos/design-system';
import { createRef } from 'react';
import type {
    ITocV1DiagnosticReportCustomControlData,
    ITocV1DiagnosticReportCustomControlDataBevindingen,
    ITocV1DiagnosticReportCustomControlDataWerkdiagnose,
} from '@common/validation/customControls/ophthalmology';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ConclusieDiagnoseOption, ProcessEntity, saveTypeEnum, ToastType } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { getDisplayName } from '@common/utils/getDisplayName';
import {
    OphthalmologyPoliciesEnum,
    UrgenciesForConsultOogartsEnum,
} from '@common/validation/customControls/ophthalmology';
import { nullDiagnosis } from '@common/validation/diagnosis';
import { InputPeriod } from '@frontend/components/elements/InputPeriod';
import { SearchableEnumDropdown } from '@frontend/components/elements/SearchableEnumDropdown';
import { translateKey } from '@frontend/translate';
import { showServiceToast } from '@frontend/utils/showToast';
import type { IDiagnosis } from '../../../../interfaces/diagnosis';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    Form,
    FormDivider,
    HeaderDivider,
    InputCheckbox,
    InputRadioGroup,
    InputTextArea,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormGroup, FormLabel, HorizontalFormGroup, InputContainer, InputControl } from '../../../../elements/forms';
import { InputTextCheckbox } from '../../../../elements/InputTextCheckbox';
import { TextAreaField } from '../../../../fields';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { TooV1SupervisionAddEdit } from '../../TOO/v1/TooV1SupervisionAddEdit';
import { addFormAnalytics, addFormConversion, addTrackFormSubmit } from '../../utils/formAnalytics';
import { Icd10Selections } from '../../utils/v1/Icd10/Icd10Selections';

interface IInnerTocV1DiagnosticReportAddEditState {
    andersCheck: boolean;
    buttonDisabled?: boolean;
    isAndersTextBevindingenChecked: boolean;
    isSaved: null | saveTypeEnum;
}

export const tocV1DiagnosticReportWerkdiagnoseLabels: {
    [K in keyof ITocV1DiagnosticReportCustomControlDataWerkdiagnose]: string;
} = {
    cataract: 'Cataract',
    diabetische_retinopathie: 'Diabetische retinopathie',
    droge_ogen: 'Droge ogen',
    glaucoom: 'Glaucoom',
    glaucoom_ja: 'Ja',
    glaucoom_nee: 'Nee',
    glaucoom_suspect: 'Suspect',
    macula_degeneratie: 'Maculadegeneratie',
    macula_droog: 'Droog',
    macula_nat: 'Nat',
    macula_suspect: 'Suspect',
    retinopathie: 'Retinopathie',
    retinopathie_overig: 'Retinopathie overig',
};

export function TocV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    return <InnerTocV1DiagnosticReportAddEdit {...props} />;
}

class InnerTocV1DiagnosticReportAddEdit extends Component<
    ICustomControlDiagnosticReportAddEditProps,
    IInnerTocV1DiagnosticReportAddEditState
> {
    public translate = getLanguage();
    private readonly formRef = createRef<HTMLFormElement>();
    private readonly saveSendButtonRef = createRef<HTMLButtonElement>();

    constructor(props: ICustomControlDiagnosticReportAddEditProps) {
        super(props);
        this.state = {
            andersCheck: false,
            isAndersTextBevindingenChecked: false,
            isSaved: null,
        };
    }

    public async componentDidMount() {
        const formNode = this.formRef.current;
        const saveSendButtonNode = this.saveSendButtonRef.current;
        addFormAnalytics(formNode, saveSendButtonNode);
    }

    public render() {
        return (
            <ProcessContainer isActiveActivity={this.props.itemState.initialIsOpen}>
                <ProcessItemHeader
                    date={this.props.process && this.props.process.dateSent}
                    isFirstItem={this.props.isFirstItem}
                    isItemOpen
                    organizationName={this.props.currentDiagnosticReport?.performerOrganization?.name}
                    practitionerName={
                        this.props.currentDiagnosticReport && this.props.currentDiagnosticReport.performerPractitioner
                            ? getDisplayName(this.props.currentDiagnosticReport.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    title={translateKey('diagnosticReport.category_consultReport')}
                />

                <Form
                    formRef={this.formRef}
                    id="TocV1DiagnosticReportAddEditId"
                    name="TocV1DiagnosticReportAddEdit"
                    onSubmit={async () => null}
                >
                    {this.addEditConsultReportForm()}

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.diagnosticReport}
                                isExisting={!!this.props.request.id}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveDiagnosticReport(saveTypeEnum.draft);
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveDiagnosticReport(saveTypeEnum.final);
                                }}
                                saveSendButtonRef={this.saveSendButtonRef}
                            />,
                        ]}
                    />
                </Form>
            </ProcessContainer>
        );
    }

    private addEditConsultReportForm() {
        const bezoekAanSpecialist = ['Ja', 'Nee'];

        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'diagnosticReport',
                    item: this.props.diagnosticReport.customControlData,
                    errors: this.props.errors,
                    onChange: async (key, value) => await this.props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await this.props.validateOneCustomControl(key),
                }}
            >
                <HorizontalFormGroup
                    hasErrors={
                        this.props.errors.has('bevindingen') ||
                        this.props.errors.has('customControlData.bevindingen.andersText')
                    }
                >
                    <FormLabel isRequired>Bevindingen</FormLabel>
                    <InputContainer>
                        {this.bevindingenOption('niet_te_beoordelen', 'Niet te beoordelen')}
                        {this.bevindingenOption('geen_afwijkingen', 'Geen afwijkingen')}
                        {this.bevindingenOption('andersText', null)}
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.heb_je_een_diagnose')}>
                    <FormLabel isRequired>Heb je een diagnose?</FormLabel>
                    <InputRadioGroup
                        error={this.props.errors.get('customControlData.heb_je_een_diagnose')}
                        name="heb_je_een_diagnose"
                        onChange={async (value) => await this.handleChangeHebJeEenDiagnose(value)}
                        onDoValidate={async () => await this.props.validateOneCustomControl('heb_je_een_diagnose')}
                        options={[
                            ConclusieDiagnoseOption.ja,
                            ConclusieDiagnoseOption.nog_geen_diagnose,
                            ConclusieDiagnoseOption.geen_afwijkingen,
                        ]}
                        value={this.props.diagnosticReport.customControlData.heb_je_een_diagnose}
                    />
                </HorizontalFormGroup>

                {this.props.diagnosticReport.customControlData.heb_je_een_diagnose === ConclusieDiagnoseOption.ja && (
                    <HorizontalFormGroup
                        hasErrors={
                            this.props.errors.has('werkdiagnose') ||
                            this.props.errors.has('customControlData.werkdiagnose.andersText')
                        }
                    >
                        <FormLabel isRequired>Werkdiagnose</FormLabel>
                        <InputContainer>
                            {this.werkdiagnoseOption('cataract')}
                            {this.werkdiagnoseOption('macula_degeneratie', [
                                'macula_nat',
                                'macula_droog',
                                'macula_suspect',
                            ])}
                            {this.werkdiagnoseOption('glaucoom', ['glaucoom_ja', 'glaucoom_nee', 'glaucoom_suspect'])}
                            {this.werkdiagnoseOption('retinopathie', [
                                'diabetische_retinopathie',
                                'retinopathie_overig',
                            ])}
                            {this.werkdiagnoseOption('droge_ogen')}
                            <InputControl>
                                <FormGroup isGrouped>
                                    <InputCheckbox
                                        checked={this.state.andersCheck}
                                        checkOnTextClickedDisabled
                                        onChange={async (value) => {
                                            await this.setState({ andersCheck: value });
                                            if (!value) {
                                                await this.clearDiagnosis();
                                            }
                                        }}
                                    />
                                    <InputContainer>
                                        <Icd10Selections
                                            bold
                                            diagnosis={this.props.diagnosticReport.diagnosis}
                                            errors={this.props.errors}
                                            hideWorkingDiagnosisLabel
                                            isWorkingDiagnosis={false}
                                            onChange={async (diagnosis) => {
                                                await this.props.handleChange('diagnosis', diagnosis);
                                                await this.setState({
                                                    andersCheck:
                                                        diagnosis.diagnosisConsiderationIcd10 !== null ||
                                                        diagnosis.considerationThesaurusTermId !== null,
                                                });
                                            }}
                                            onDoValidate={async () => {
                                                await this.props.validateOne('diagnosis');
                                                await this.props.validateOneCustomControl('werkdiagnose');
                                            }}
                                            placeholder={'Anders, zoek uw diagnose'}
                                            specialismCode={this.props.service.specialismCode || ''}
                                        />
                                    </InputContainer>
                                </FormGroup>
                            </InputControl>
                        </InputContainer>
                    </HorizontalFormGroup>
                )}

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.antwoord')}>
                    <FormLabel>Antwoord</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            error={this.props.errors.get('customControlData.antwoord')}
                            onChange={async (value) => {
                                await this.props.handleChangeCustomControl({ antwoord: value });
                            }}
                            onDoValidate={async () => await this.props.validateOneCustomControl('antwoord')}
                            placeholder="Omschrijf je antwoord op de vraag"
                            value={this.props.diagnosticReport.customControlData.antwoord}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <FormDivider />

                <HeaderDivider text="Advies" />

                <NewFormGroup
                    hasErrors={
                        this.props.errors.has('customControlData.beleid') ||
                        this.props.errors.has('customControlData.beleid_anders')
                    }
                    isRequired={true}
                    label="Beleid"
                >
                    <SearchableEnumDropdown
                        automation="beleid"
                        error={this.props.errors.get('customControlData.beleid')}
                        jsEnum={OphthalmologyPoliciesEnum}
                        onChange={async (value) => {
                            await this.props.handleChangeCustomControl({ beleid: value });
                            await this.props.handleChangeCustomControl({ beleid_anders: null });
                            await this.props.handleChangeCustomControl({ urgentie_anders: null });
                            await this.props.handleChangeCustomControl({ urgentie: null });
                        }}
                        onDoValidate={async () => {
                            await this.props.validateOneCustomControl('beleid');
                            if (this.props.errors.has('customControlData.beleid_anders')) {
                                await this.props.validateOneCustomControl('beleid_anders');
                            }
                        }}
                        placeholder="Kies uit de lijst"
                        value={this.props.diagnosticReport.customControlData.beleid}
                    />
                </NewFormGroup>

                {this.props.diagnosticReport.customControlData.beleid === OphthalmologyPoliciesEnum.anders && (
                    <TextAreaField
                        error={this.props.errors.get('customControlData.beleid_anders')}
                        fieldKey="beleid_anders"
                        hasErrors={this.props.errors.has('customControlData.beleid_anders')}
                        label=" "
                        placeholder="Beleid"
                    />
                )}

                {this.props.diagnosticReport.customControlData.beleid ===
                    OphthalmologyPoliciesEnum.revisieOptometrist && (
                    <NewFormGroup
                        hasErrors={this.props.errors.has('customControlData.urgentie')}
                        isRequired={true}
                        label="Urgentie"
                    >
                        <InputPeriod
                            {...this.props}
                            errors={this.props.errors.get('customControlData.urgentie')}
                            fieldKey="urgentie"
                            value={this.props.diagnosticReport.customControlData.urgentie}
                        />
                    </NewFormGroup>
                )}
                {[
                    OphthalmologyPoliciesEnum.consultOogartsDoorHuisarts,
                    OphthalmologyPoliciesEnum.consultOogartsDoorOptometrist,
                ].includes(this.props.diagnosticReport.customControlData.beleid) && (
                    <NewFormGroup
                        hasErrors={
                            this.props.errors.has('customControlData.urgentie') ||
                            this.props.errors.has('customControlData.urgentie_anders')
                        }
                        isRequired={true}
                        label="Urgentie"
                    >
                        <SearchableEnumDropdown
                            automation="urgentie"
                            error={this.props.errors.get('customControlData.urgentie')}
                            jsEnum={UrgenciesForConsultOogartsEnum}
                            onChange={async (value) => {
                                await this.props.handleChangeCustomControl({ urgentie: value });
                                await this.props.handleChangeCustomControl({ urgentie_anders: null });
                            }}
                            onDoValidate={async () => {
                                await this.props.validateOneCustomControl('urgentie');
                                if (this.props.errors.has('customControlData.urgentie_anders')) {
                                    await this.props.validateOneCustomControl('urgentie_anders');
                                }
                            }}
                            placeholder="Kies uit de lijst"
                            value={this.props.diagnosticReport.customControlData.urgentie}
                        />
                    </NewFormGroup>
                )}

                {this.props.diagnosticReport.customControlData.urgentie === UrgenciesForConsultOogartsEnum.anders && (
                    <TextAreaField
                        error={this.props.errors.get('customControlData.urgentie_anders')}
                        fieldKey="urgentie_anders"
                        hasErrors={this.props.errors.has('customControlData.urgentie_anders')}
                        label=" "
                        placeholder="Urgentie"
                    />
                )}
                <HorizontalFormGroup
                    hasErrors={this.props.errors.has('customControlData.bezoek_aan_specialist_nu_nodig')}
                >
                    <FormLabel isRequired>{translateKey('diagnosticReport.visitToSpecialistNecessary')}</FormLabel>
                    <InputRadioGroup
                        error={this.props.errors.get('customControlData.bezoek_aan_specialist_nu_nodig')}
                        name="bezoek_aan_specialist_nu_nodig"
                        onChange={async (value) =>
                            await this.props.handleChangeCustomControl({ bezoek_aan_specialist_nu_nodig: value })
                        }
                        onDoValidate={async () => {
                            await this.props.validateOneCustomControl('bezoek_aan_specialist_nu_nodig');
                        }}
                        options={bezoekAanSpecialist}
                        value={this.props.diagnosticReport.customControlData.bezoek_aan_specialist_nu_nodig}
                    />
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                    <FormLabel>Toelichting</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            automation="remark"
                            error={this.props.errors.get('remark')}
                            onChange={async (value) => await this.props.handleChange('remark', value)}
                            onDoValidate={async () => await this.props.validateOne('remark')}
                            placeholder="Een toelichting, opmerkingen of vragen"
                            value={this.props.diagnosticReport.remark}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <TooV1SupervisionAddEdit {...this.props} />
            </FormContextProvider>
        );
    }

    private bevindingenOption<K extends keyof ITocV1DiagnosticReportCustomControlDataBevindingen>(
        name: K,
        text: string | null
    ) {
        const bevindingen = this.props.diagnosticReport.customControlData.bevindingen;
        const nameOd = (name + '_od') as keyof ITocV1DiagnosticReportCustomControlDataBevindingen;
        const nameOs = (name + '_os') as keyof ITocV1DiagnosticReportCustomControlDataBevindingen;

        return (
            <>
                <div className="row">
                    {name === 'andersText' && (
                        <div className="col">
                            <InputTextCheckbox
                                error={this.props.errors.get('customControlData.bevindingen.andersText')}
                                expandableTextArea={true}
                                hasFormControl
                                labelStyle={{ width: '100%', display: 'block' }}
                                onChange={async (value, checked) => {
                                    await this.handleCheckboxBevindingen(
                                        name,
                                        value as ITocV1DiagnosticReportCustomControlDataBevindingen[K],
                                        checked
                                    );
                                }}
                                onDoValidate={async () => {
                                    await this.props.validateOneCustomControl('bevindingen');
                                    await this.props.validateOneCustomControl('bevindingen.andersText');
                                }}
                                placeholder="Anders, namelijk..."
                                text={bevindingen.andersText}
                            />
                        </div>
                    )}

                    {name !== 'andersText' && (
                        <div className="col-sm-12 col-xl-8">
                            <InputCheckbox
                                checked={bevindingen[name]}
                                onChange={async (value) => {
                                    await this.handleCheckboxBevindingen(
                                        name,
                                        value as ITocV1DiagnosticReportCustomControlDataBevindingen[K]
                                    );
                                }}
                                onDoValidate={async () => await this.props.validateOneCustomControl('bevindingen')}
                                text={text}
                            />
                        </div>
                    )}
                </div>
                {(bevindingen[name] || (name === 'andersText' && this.state.isAndersTextBevindingenChecked)) && (
                    <InputControl indented>
                        <InputCheckbox
                            checked={bevindingen[nameOd]}
                            onChange={async (value) => await this.handleChangeBevindingen(nameOd, value)}
                            onDoValidate={async () => await this.props.validateOneCustomControl('bevindingen')}
                            text="OD"
                        />
                        <InputCheckbox
                            checked={bevindingen[nameOs]}
                            onChange={async (value) => await this.handleChangeBevindingen(nameOs, value)}
                            onDoValidate={async () => await this.props.validateOneCustomControl('bevindingen')}
                            text="OS"
                        />
                    </InputControl>
                )}
            </>
        );
    }

    private werkdiagnoseOption<K extends keyof ITocV1DiagnosticReportCustomControlDataWerkdiagnose>(
        name: K,
        suboptions?: K[]
    ) {
        const werkdiagnose: ITocV1DiagnosticReportCustomControlDataWerkdiagnose =
            this.props.diagnosticReport.customControlData.werkdiagnose;

        return (
            <>
                <InputCheckbox
                    automation={tocV1DiagnosticReportWerkdiagnoseLabels[name]}
                    checked={werkdiagnose[name] !== false && werkdiagnose[name] !== null}
                    onChange={async (value) => {
                        await this.handleChangeWerkdiagnose(
                            name,
                            value as ITocV1DiagnosticReportCustomControlDataWerkdiagnose[K]
                        );

                        if (suboptions) {
                            for (const option of suboptions) {
                                await this.handleChangeWerkdiagnose(
                                    option,
                                    false as ITocV1DiagnosticReportCustomControlDataWerkdiagnose[K]
                                );
                            }
                        }
                    }}
                    onDoValidate={async () => await this.props.validateOneCustomControl('werkdiagnose')}
                    text={tocV1DiagnosticReportWerkdiagnoseLabels[name]}
                />

                {werkdiagnose[name] &&
                    suboptions &&
                    suboptions.map((option) => (
                        <InputControl indented>
                            <InputCheckbox
                                checked={werkdiagnose[option] !== false && werkdiagnose[option] !== null}
                                key={option}
                                onChange={async (value) =>
                                    await this.handleChangeWerkdiagnose(
                                        option,
                                        value as ITocV1DiagnosticReportCustomControlDataWerkdiagnose[K]
                                    )
                                }
                                onDoValidate={async () => await this.props.validateOneCustomControl('werkdiagnose')}
                                text={tocV1DiagnosticReportWerkdiagnoseLabels[option]}
                            />
                        </InputControl>
                    ))}
            </>
        );
    }

    private async handleCheckboxBevindingen<K extends keyof ITocV1DiagnosticReportCustomControlDataBevindingen>(
        name: K,
        value: ITocV1DiagnosticReportCustomControlDataBevindingen[K],
        checked?: boolean
    ) {
        const nameOd = (name + '_od') as keyof ITocV1DiagnosticReportCustomControlDataBevindingen;
        const nameOs = (name + '_os') as keyof ITocV1DiagnosticReportCustomControlDataBevindingen;

        await this.handleChangeBevindingen(name, value);
        await this.handleChangeBevindingen(nameOd, false);
        await this.handleChangeBevindingen(nameOs, false);

        if (name === 'andersText' && checked !== undefined && checked !== this.state.isAndersTextBevindingenChecked) {
            await this.setState({ isAndersTextBevindingenChecked: checked });
        }
    }

    private async saveDiagnosticReport(saveType: saveTypeEnum) {
        await this.setState({ buttonDisabled: true });
        addTrackFormSubmit(this.saveSendButtonRef.current);

        const validatedDiagnosticReport = await this.props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        const diagnosticReportId = await this.props.onSaveDiagnosticReport(saveType);

        if (saveType === saveTypeEnum.draft) {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'diagnosticReport',
                translateKey('toast.draftDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'diagnosticReport',
                translateKey('toast.answerSentToOrganizationSuccess', this.props.request.requesterOrganization?.name)
            );
            addFormConversion(this.formRef.current);
        }

        await this.setState({ buttonDisabled: false });
        await this.props.onSave(diagnosticReportId);
        this.setState({ isSaved: saveType });
    }

    private async handleChangeBevindingen<K extends keyof ITocV1DiagnosticReportCustomControlDataBevindingen>(
        name: K,
        value: ITocV1DiagnosticReportCustomControlDataBevindingen[K]
    ) {
        await this.props.handleChangeCustomControl({
            bevindingen: {
                ...this.props.diagnosticReport.customControlData.bevindingen,
                [name]: value,
            },
        });

        if (
            this.props.diagnosticReport.customControlData.bevindingen.niet_te_beoordelen &&
            !this.props.diagnosticReport.customControlData.bevindingen.geen_afwijkingen
        ) {
            await this.resetWerkdiagnose();
            await this.props.handleChangeCustomControl({
                heb_je_een_diagnose: ConclusieDiagnoseOption.nog_geen_diagnose,
            });
        } else if (
            this.props.diagnosticReport.customControlData.bevindingen.geen_afwijkingen ||
            (this.props.diagnosticReport.customControlData.bevindingen.niet_te_beoordelen &&
                this.props.diagnosticReport.customControlData.bevindingen.geen_afwijkingen)
        ) {
            await this.resetWerkdiagnose();
            await this.props.handleChangeCustomControl({
                heb_je_een_diagnose: ConclusieDiagnoseOption.geen_afwijkingen,
            });
        }
    }

    private async handleChangeWerkdiagnose<K extends keyof ITocV1DiagnosticReportCustomControlDataWerkdiagnose>(
        name: K,
        value: ITocV1DiagnosticReportCustomControlDataWerkdiagnose[K]
    ) {
        await this.props.handleChangeCustomControl({
            werkdiagnose: {
                ...this.props.diagnosticReport.customControlData.werkdiagnose,
                [name]: value,
            },
        });
    }

    private async handleChangeHebJeEenDiagnose(value: string | null) {
        await this.resetWerkdiagnose();
        await this.props.handleChangeCustomControl({ heb_je_een_diagnose: value });
    }

    private async resetWerkdiagnose() {
        await this.clearDiagnosis();
        await this.setState({ andersCheck: false });
        await this.props.handleChangeCustomControl({
            werkdiagnose: { ...tocV1DiagnosticReportInitializeCustomControlData().werkdiagnose },
        });
    }

    private async clearDiagnosis() {
        await this.props.handleChange('diagnosis', { ...nullDiagnosis });
    }
}

function validateBevindingen(customControlData: ITocV1DiagnosticReportCustomControlData, errors: ValidationErrors) {
    const bevindingenFilledIn = Object.values(customControlData.bevindingen).some((value) => {
        return value !== false && value !== null;
    });

    return bevindingenFilledIn ? errors.remove('bevindingen') : errors.set('bevindingen', 'any_required');
}

function validateWerkdiagnose(
    customControlData: ITocV1DiagnosticReportCustomControlData,
    errors: ValidationErrors,
    diagnosis?: IDiagnosis
) {
    if (
        customControlData.heb_je_een_diagnose === ConclusieDiagnoseOption.geen_afwijkingen ||
        customControlData.heb_je_een_diagnose === ConclusieDiagnoseOption.nog_geen_diagnose
    ) {
        return errors.remove('werkdiagnose');
    }

    const werkdiagnoseFilledIn = Object.values(customControlData.werkdiagnose).some((value) => {
        return (
            value !== false || diagnosis?.diagnosisConsiderationIcd10 || diagnosis?.diagnosisConsiderationIcd10fallback
        );
    });

    return werkdiagnoseFilledIn ? errors.remove('werkdiagnose') : errors.set('werkdiagnose', 'any_required');
}

export function tocV1DiagnosticReportValidateOneCustomControlData(
    name: string,
    customControlData: ITocV1DiagnosticReportCustomControlData,
    errors: ValidationErrors,
    diagnosis?: IDiagnosis
) {
    if (name === 'bevindingen') {
        return validateBevindingen(customControlData, errors);
    } else if (name === 'werkdiagnose') {
        return validateWerkdiagnose(customControlData, errors, diagnosis);
    }

    return errors;
}

export function tocV1DiagnosticReportValidateCustomControlData(
    customControlData: ITocV1DiagnosticReportCustomControlData,
    errors: ValidationErrors,
    diagnosis?: IDiagnosis
) {
    errors = validateBevindingen(customControlData, errors);
    errors = validateWerkdiagnose(customControlData, errors, diagnosis);

    return errors;
}

export function tocV1DiagnosticReportInitializeCustomControlData() {
    return {
        antwoord: null,
        beleid: null,
        beleid_anders: null,
        bevindingen: {
            andersText: null,
            andersText_od: false,
            andersText_os: false,
            geen_afwijkingen: false,
            geen_afwijkingen_od: false,
            geen_afwijkingen_os: false,
            niet_te_beoordelen: false,
            niet_te_beoordelen_od: false,
            niet_te_beoordelen_os: false,
        },
        bezoek_aan_specialist_nu_nodig: null,
        hasSupervisionFields: false,
        heb_je_een_diagnose: null,
        urgentie: null,
        urgentie_anders: null,
        werkdiagnose: {
            cataract: false,
            diabetische_retinopathie: false,
            droge_ogen: false,
            glaucoom: false,
            glaucoom_ja: false,
            glaucoom_nee: false,
            glaucoom_suspect: false,
            macula_degeneratie: false,
            macula_droog: false,
            macula_nat: false,
            macula_suspect: false,
            retinopathie: false,
            retinopathie_overig: false,
        },
    };
}
